import React, { useEffect, useRef, useState } from 'react';
import PartnerInfo from './partner-info';
import PartnersHeader from './partners-header';
import AddPartnerSideModal from './add-partner';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import InfiniteScroll from 'react-infinite-scroll-component';
import PartnersCard from 'components/logistics/partners/partners-card';
import { PartnerType } from 'models/partner.model';
import { apiService } from 'utils/api';
import debounce from 'lodash.debounce';
import '../partners/style.scss';
import useDashboardContext from 'contexts/dashboard.context';

const Partners = () => {
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [activePartner, setActive] = useState<any>();
  const [partners, setPartners] = useState<PartnerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchVal, setSearchVal] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [totalLength, setTotalLength] = useState(0);
  const debouncedSearch = useRef<any>();
  const { activeCokitchen } = useDashboardContext();
  const [data, setData] = useState({
    page: 1,
    search: '',
    cokitchen_id: [activeCokitchen?.id] as string[]
  });

  const pageHasLoaded = useRef(false);

  const loadPartners = async () => {
    try {
      if (data.page === 1) setLoading(true);
      const { cokitchen_id, page, search } = data;
      const res = await apiService.post(`logistics/delivery-partner`, {
        data: { cokitchen_id: cokitchen_id, page: page, search: search ? search : undefined },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const results = res.data?.delivery_partners?.results ?? [];
      const newPartners = data.page === 1 ? results : [...partners, ...results];
      setTotalLength(res.data?.delivery_partners?.total ?? 0);
      setPartners(newPartners ?? []);
    } catch {
      setPartners([]);
      toast(<ToastMessage message='Error loading partners' type='error' />);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = (id: string) => {
    setPartners(partners.filter((p) => p.id !== id));
  };

  useEffect(() => {
    if (pageHasLoaded.current) loadPartners();
    pageHasLoaded.current = true;
  }, [data]);

  const handleDebouncedSearch = (search: string) => {
    setData({ ...data, page: 1, search });
  };

  useEffect(() => {
    debouncedSearch.current = debounce(handleDebouncedSearch, 1000);
  }, []);

  useEffect(() => {
    debouncedSearch.current(searchVal);
  }, [searchVal]);

  const fetchMoreData = () => {
    const newdata = {
      ...data,
      search: data.search,
      page: data.page + 1
    };
    setData(newdata);
  };

  useEffect(() => {
    setHasMore(partners.length < totalLength);
    if (partners.length > 0) setLoading(false);
  }, [partners]);

  return (
    <div>
      <PartnersHeader
        search={searchVal}
        setSearch={setSearchVal}
        count={totalLength}
        onClick={() => setShowAddPartner(true)}
        selectedCokitchen={data.cokitchen_id}
        setSelectedCokitchen={(value) => {
          setData({
            ...data,
            cokitchen_id: value,
            page: 1
          });
        }}
      />
      {loading ? (
        <div className='h-[50vh] flex items-center justify-center'>
          <h2 className='font-bold text-center mx-auto text-lg md:text-2xl'>Loading...</h2>
        </div>
      ) : partners.length === 0 ? (
        <div className='h-[50vh] flex items-center justify-center'>
          <h2 className='font-bold text-center mx-auto text-lg md:text-2xl'>
            No partners available {data.search ? `with search "${data.search}"` : ''}
          </h2>
        </div>
      ) : (
        <InfiniteScroll
          hasMore={hasMore}
          next={() => fetchMoreData()}
          dataLength={partners.length}
          loader={<p className='text-center mt-8'>Loading more partners...</p>}
          endMessage={<p className='text-center mt-8'>End of list</p>}>
          <div className={`flex justify-between gap-4 mt-8 w-full`}>
            <div className='grid w-full gap-5 h-full md:grid-cols-2 lg:grid-cols-3'>
              {partners.map((p) => (
                <PartnersCard
                  key={p.id}
                  isActive={p.id === activePartner}
                  onClick={() => {
                    setActive((prev: any) => (prev === p.id ? null : p.id));
                  }}
                  partner={p}
                  handleDeletePartner={handleDeletePartner}
                />
              ))}
            </div>
            {activePartner && (
              <PartnerInfo
                partner={partners.find((partner) => partner.id === activePartner)}
                onClose={() => setActive(undefined)}
              />
            )}
          </div>
        </InfiniteScroll>
      )}
      <AddPartnerSideModal
        showAddPartner={showAddPartner}
        onClose={() => setShowAddPartner(false)}
      />
    </div>
  );
};

export default Partners;
